/**
 * Paliers d'étoiles (1–5) → bonus.
 *
 * **Pilote** (combativité, virage) — alternance saison / permanent :
 * 1★ = +0 ; 2★ = +1 saison ; 3★ = +1 permanent ; 4★ = +2 saison ; 5★ = +2 permanent.
 *
 * **Voiture** (départ, ligne droite) — bonus permanents linéaires :
 * 1★ = +0 ; 2★ = +1 ; 3★ = +2 ; 4★ = +3 ; 5★ = +4.
 */

export const STAR_LEVEL_MIN = 0;
export const STAR_LEVEL_MAX = 5;

/** Plage des caractéristiques en jeu (entier 1–5). */
export const CHAMPIONSHIP_STAR_MIN = 1;
export const CHAMPIONSHIP_STAR_MAX = 5;

export interface StarTierBonus {
  seasonal: number;
  permanent: number;
}

export function starLevelToBonuses(level: number): StarTierBonus {
  const l = clampStarLevel(level);
  if (l <= 0) return { seasonal: 0, permanent: 0 };
  if (l === 1) return { seasonal: 0, permanent: 0 };
  if (l % 2 === 0) return { seasonal: l / 2, permanent: 0 };
  return { seasonal: 0, permanent: (l - 1) / 2 };
}

export function clampStarLevel(level: number): number {
  if (!Number.isFinite(level)) return 0;
  return Math.min(STAR_LEVEL_MAX, Math.max(STAR_LEVEL_MIN, Math.round(level)));
}

export function clampChampionshipStarLevel(level: number): number {
  return Math.min(CHAMPIONSHIP_STAR_MAX, Math.max(CHAMPIONSHIP_STAR_MIN, Math.round(level)));
}

/**
 * Bonus effectif au palier `level` (1–5) : valeur du palier atteint uniquement.
 * Pairs (2, 4) = bonus saison ; impairs (3, 5) = permanent ; 1★ = aucun bonus.
 */
export function effectiveBonusAtLevel(level: number, seasonalBonusesActive = true): number {
  const l = clampStarLevel(level);
  if (l <= 0) return 0;
  const tier = starLevelToBonuses(l);
  if (l % 2 === 0) {
    return seasonalBonusesActive ? tier.seasonal : 0;
  }
  return tier.permanent;
}

/** Bonus de course pour une caractéristique pilote notée 1–5★ (paliers saison / permanent). */
export function effectiveBonusFromChampionshipStars(
  stars: number,
  seasonalBonusesActive = true
): number {
  return effectiveBonusAtLevel(clampChampionshipStarLevel(stars), seasonalBonusesActive);
}

/** Bonus de course pour une caractéristique voiture notée 1–5★ (linéaire, toujours actif). */
export function effectiveCarBonusFromChampionshipStars(stars: number): number {
  const level = clampChampionshipStarLevel(stars);
  return Math.max(0, level - 1);
}

/** Cumul permanent seul (fin de saison). */
export function permanentBonusAtLevel(level: number): number {
  const l = clampStarLevel(level);
  let total = 0;
  for (let i = 1; i <= l; i++) {
    total += starLevelToBonuses(i).permanent;
  }
  return total;
}

/** Ancienne note 2–10 ou 1–5 hétérogène → 1–5. */
export function migrateLegacyStatToStars(oldStat: number): number {
  const n = Math.round(oldStat);
  if (n > CHAMPIONSHIP_STAR_MAX) {
    return clampChampionshipStarLevel(Math.round(n / 2));
  }
  return clampChampionshipStarLevel(n);
}

/** @deprecated */
export interface SeasonBonusContext {
  seasonActive?: boolean;
}

/** @deprecated */
export function effectiveBonusFromStarLevel(level: number, ctx?: SeasonBonusContext): number {
  return effectiveBonusAtLevel(level, ctx?.seasonActive !== false);
}
